import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  fetchProductAsync,
  updateProductAsync,
  clearCurrentProduct
} from '../features/products/productsSlice';
import WholesalerNavbar from '../features/wholesaler/components/WholesalerNavbar';
import { categories, purityOptions } from '../data/options';
import { FaUpload, FaTrash, FaImage, FaVideo } from 'react-icons/fa';

const WholesalerProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentProduct, loading, error } = useSelector((state) => state.products);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProduct, setEditedProduct] = useState(null);
  const [errors, setErrors] = useState({});
  const [previews, setPreviews] = useState({
    cover_image: null,
    image2: null,
    image3: null,
    video: null
  });
  const [selectedMedia, setSelectedMedia] = useState('cover_image');

  // Check if we're in edit mode from URL query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get('mode');
    if (mode === 'edit') {
      setIsEditing(true);
    }
  }, [location.search]);

  useEffect(() => {
    // Fetch product details when component mounts
    dispatch(fetchProductAsync(productId));
    
    // Cleanup function to clear current product when component unmounts
    return () => {
      dispatch(clearCurrentProduct());
    };
  }, [dispatch, productId]);

  useEffect(() => {
    // Set edited product when current product changes
    if (currentProduct) {
      setEditedProduct(currentProduct);
      setPreviews({
        cover_image: currentProduct.cover_image,
        image2: currentProduct.image2,
        image3: currentProduct.image3,
        video: currentProduct.video
      });
      setSelectedMedia('cover_image');
    }
  }, [currentProduct]);

  const validateFile = (file, type) => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/mp3'];
    const videoTypes = ['video/mp4'];
    
    if (type === 'image' && !imageTypes.includes(file.type)) {
      return 'Please upload a valid image file (jpg, jpeg, mp3)';
    }
    if (type === 'video' && !videoTypes.includes(file.type)) {
      return 'Please upload a valid video file (mp4)';
    }
    return null;
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const error = validateFile(file, type);
    if (error) {
      setErrors(prev => ({ ...prev, [type]: error }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'cover_image') {
        setEditedProduct(prev => ({ ...prev, cover_image: file }));
        setPreviews(prev => ({ ...prev, cover_image: reader.result }));
      } else if (type === 'image2') {
        setEditedProduct(prev => ({ ...prev, image2: file }));
        setPreviews(prev => ({ ...prev, image2: reader.result }));
      } else if (type === 'image3') {
        setEditedProduct(prev => ({ ...prev, image3: file }));
        setPreviews(prev => ({ ...prev, image3: reader.result }));
      } else if (type === 'video') {
        setEditedProduct(prev => ({ ...prev, video: file }));
        setPreviews(prev => ({ ...prev, video: reader.result }));
      }
      setErrors(prev => ({ ...prev, [type]: null }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = (type) => {
    if (type === 'cover_image') {
      setEditedProduct(prev => ({ ...prev, cover_image: null }));
      setPreviews(prev => ({ ...prev, cover_image: null }));
    } else if (type === 'image2') {
      setEditedProduct(prev => ({ ...prev, image2: null }));
      setPreviews(prev => ({ ...prev, image2: null }));
    } else if (type === 'image3') {
      setEditedProduct(prev => ({ ...prev, image3: null }));
      setPreviews(prev => ({ ...prev, image3: null }));
    } else if (type === 'video') {
      setEditedProduct(prev => ({ ...prev, video: null }));
      setPreviews(prev => ({ ...prev, video: null }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!editedProduct.name || !editedProduct.category || !editedProduct.cover_image || !editedProduct.quantity || !editedProduct.weight) {
      setErrors(prev => ({
        ...prev,
        name: !editedProduct.name ? 'Product Name is required' : null,
        category: !editedProduct.category ? 'Category is required' : null,
        cover_image: !editedProduct.cover_image ? 'Thumbnail Image is required' : null,
        quantity: !editedProduct.quantity ? 'Quantity Cannot be Zero' : null,
        weight: !editedProduct.weight ? 'Weight Cannot be Zero' : null,
      }));
      return;
    }

    try {
      // Create an object to store only changed fields
      const changedFields = {};

      // Check each field for changes
      Object.keys(editedProduct).forEach(key => {
        // Skip product_id as it's not needed for update
        if (key === 'product_id') return;

        // Handle media fields differently
        if (['cover_image', 'image2', 'image3', 'video'].includes(key)) {
          // If the edited field is a File, it means it was changed
          if (editedProduct[key] instanceof File) {
            changedFields[key] = editedProduct[key];
          }
          return;
        }

        // For non-media fields, compare values
        if (JSON.stringify(editedProduct[key]) !== JSON.stringify(currentProduct[key])) {
          changedFields[key] = editedProduct[key];
        }
      });

      // Log the changes being sent

      // Only send the changed fields with product_id from URL
      const prod = await dispatch(updateProductAsync({
        product_id: productId,
        ...changedFields
      })).unwrap();

      // Add cache-busting parameter to image URLs
      const timestamp = new Date().getTime();
      const updatedProduct = {
        ...prod,
        cover_image: prod.cover_image ? `${prod.cover_image}?t=${timestamp}` : null,
        image2: prod.image2 ? `${prod.image2}?t=${timestamp}` : null,
        image3: prod.image3 ? `${prod.image3}?t=${timestamp}` : null,
        video: prod.video ? `${prod.video}?t=${timestamp}` : null
      };

      setIsEditing(false);
      setEditedProduct(updatedProduct);
      setPreviews({
        cover_image: updatedProduct.cover_image,
        image2: updatedProduct.image2,
        image3: updatedProduct.image3,
        video: updatedProduct.video
      });
      
      // Remove the mode parameter from the URL
      navigate(`/wholesaler/inventory/${productId}`);
    } catch (error) {
      console.error('Failed to update product:', error);
      if (error.message) {
        console.error('Error message:', error.message);
      }
      if (error.stack) {
        console.error('Error stack:', error.stack);
      }
    }
  };

  const handleCancel = () => {
    setEditedProduct(currentProduct);
    setIsEditing(false);
    // Remove the mode parameter from the URL
    navigate(`/wholesaler/inventory/${productId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <WholesalerNavbar />
        <div className="pt-20 max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <WholesalerNavbar />
        <div className="pt-20 max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">{error}</h3>
              <button
                onClick={() => navigate('/wholesaler/inventory')}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Back to Inventory
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentProduct || !editedProduct) {
    return (
      <div className="min-h-screen bg-gray-50">
        <WholesalerNavbar />
        <div className="pt-20 max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">Product not found</h3>
              <button
                onClick={() => navigate('/wholesaler/inventory')}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Back to Inventory
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <WholesalerNavbar />
      <div className="pt-20 max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Media Section */}
              <div className="space-y-4">
                {/* Main Media Display */}
                <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200">
                  {selectedMedia === 'video' ? (
                    <video
                      src={previews.video}
                      controls
                      className="h-full w-full object-cover object-center"
                    />
                  ) : (
                    <img
                      src={previews[selectedMedia]}
                      alt={editedProduct.name}
                      className="h-full w-full object-cover object-center"
                    />
                  )}
                </div>

                {/* Thumbnail Gallery */}
                <div className="grid grid-cols-4 gap-2">
                  {/* Cover Image Thumbnail */}
                  <div
                    className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                      selectedMedia === 'cover_image' ? 'ring-2 ring-yellow-500' : ''
                    }`}
                    onClick={() => setSelectedMedia('cover_image')}
                  >
                    <img
                      src={previews.cover_image}
                      alt="Cover"
                      className="h-full w-full object-cover object-center"
                    />
                  </div>

                  {/* Image 2 Thumbnail */}
                  {previews.image2 && (
                    <div
                      className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                        selectedMedia === 'image2' ? 'ring-2 ring-yellow-500' : ''
                      }`}
                      onClick={() => setSelectedMedia('image2')}
                    >
                      <img
                        src={previews.image2}
                        alt="Image 2"
                        className="h-full w-full object-cover object-center"
                      />
                    </div>
                  )}

                  {/* Image 3 Thumbnail */}
                  {previews.image3 && (
                    <div
                      className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                        selectedMedia === 'image3' ? 'ring-2 ring-yellow-500' : ''
                      }`}
                      onClick={() => setSelectedMedia('image3')}
                    >
                      <img
                        src={previews.image3}
                        alt="Image 3"
                        className="h-full w-full object-cover object-center"
                      />
                    </div>
                  )}

                  {/* Video Thumbnail */}
                  {previews.video && (
                    <div
                      className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer relative ${
                        selectedMedia === 'video' ? 'ring-2 ring-yellow-500' : ''
                      }`}
                      onClick={() => setSelectedMedia('video')}
                    >
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                        <FaVideo className="text-white text-3xl" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Details Section */}
              <div className="space-y-6">
                {isEditing ? (
                  <form className="space-y-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Product Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={editedProduct.name}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="weight"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Weight (g)
                        </label>
                        <input
                          type="number"
                          name="weight"
                          id="weight"
                          value={editedProduct.weight}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="purity"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Purity
                        </label>
                        <select
                          name="purity"
                          id="purity"
                          value={editedProduct.purity}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm text-sm py-1"
                          style={{ 
                            maxHeight: '32px',
                            overflow: 'hidden'
                          }}
                        >
                          {purityOptions.map((purity) => (
                            <option 
                              key={purity.id} 
                              value={purity.name} 
                              className="text-sm py-0.5"
                              style={{ height: '24px' }}
                            >
                              {purity.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="making"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Making Charge
                        </label>
                        <input
                          type="number"
                          name="making"
                          id="making"
                          value={editedProduct.making}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="quantity"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Quantity
                        </label>
                        <input
                          type="number"
                          name="quantity"
                          id="quantity"
                          value={editedProduct.quantity}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="category"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Category
                      </label>
                      <select
                        name="category"
                        id="category"
                        value={editedProduct.category}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm text-sm py-1"
                        style={{ 
                          maxHeight: '32px',
                          overflow: 'hidden'
                        }}
                      >
                        {categories.map((category) => (
                          <option 
                            key={category.id} 
                            value={category.name.toLowerCase()} 
                            className="text-sm py-0.5"
                            style={{ height: '24px' }}
                          >
                            {category.name}
                          </option>
                        ))}
                      </select>
                      {errors.category && (
                        <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Description
                      </label>
                      <textarea
                        name="description"
                        id="description"
                        rows={4}
                        value={editedProduct.description}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Thumbnail Image *
                      </label>
                      <div className="mt-1 flex items-center space-x-4">
                        <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 cursor-pointer">
                          <FaUpload className="mr-2" />
                          Upload Thumbnail
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.mp3"
                            onChange={(e) => handleFileUpload(e, 'cover_image')}
                          />
                        </label>
                        {previews.cover_image && (
                          <div className="flex items-center space-x-2">
                            <FaImage className="text-green-500" />
                            <span className="text-sm text-gray-600">
                              {editedProduct.cover_image.name || 'Current Thumbnail'} uploaded
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile('cover_image')}
                              className="text-red-600 hover:text-red-800"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        )}
                      </div>
                      {errors.cover_image && (
                        <p className="mt-1 text-sm text-red-600">{errors.cover_image}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Additional Images
                      </label>
                      <div className="mt-1 space-y-4">
                        {/* Image 2 Upload */}
                        <div className="flex items-center space-x-4">
                          {!editedProduct.image2 ? (
                            <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 cursor-pointer">
                              <FaUpload className="mr-2" />
                              Upload Image 2
                              <input
                                type="file"
                                className="hidden"
                                accept=".jpg,.jpeg,.mp3"
                                onChange={(e) => handleFileUpload(e, 'image2')}
                              />
                            </label>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <FaImage className="text-green-500" />
                              <span className="text-sm text-gray-600">
                                {editedProduct.image2.name || 'Image 2'} uploaded
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveFile('image2')}
                                className="text-red-600 hover:text-red-800"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Image 3 Upload */}
                        <div className="flex items-center space-x-4">
                          {!editedProduct.image3 ? (
                            <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 cursor-pointer">
                              <FaUpload className="mr-2" />
                              Upload Image 3
                              <input
                                type="file"
                                className="hidden"
                                accept=".jpg,.jpeg,.mp3"
                                onChange={(e) => handleFileUpload(e, 'image3')}
                              />
                            </label>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <FaImage className="text-green-500" />
                              <span className="text-sm text-gray-600">
                                {editedProduct.image3.name || 'Image 3'} uploaded
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveFile('image3')}
                                className="text-red-600 hover:text-red-800"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {errors.image2 && (
                        <p className="mt-1 text-sm text-red-600">{errors.image2}</p>
                      )}
                      {errors.image3 && (
                        <p className="mt-1 text-sm text-red-600">{errors.image3}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Product Video
                      </label>
                      <div className="mt-1 flex items-center space-x-4">
                        {!editedProduct.video ? (
                          <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 cursor-pointer">
                            <FaUpload className="mr-2" />
                            Upload Video
                            <input
                              type="file"
                              className="hidden"
                              accept=".mp4"
                              onChange={(e) => handleFileUpload(e, 'video')}
                            />
                          </label>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <FaVideo className="text-green-500" />
                            <span className="text-sm text-gray-600">
                              {editedProduct.video.name || 'Current Video'} uploaded
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile('video')}
                              className="text-red-600 hover:text-red-800"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        )}
                      </div>
                      {errors.video && (
                        <p className="mt-1 text-sm text-red-600">{errors.video}</p>
                      )}
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-6">
                    <div className="flex justify-between items-start">
                      <h2 className="text-2xl font-bold text-gray-900">
                        {editedProduct.name}
                      </h2>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        Edit Product
                      </button>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        {editedProduct.weight && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Weight</h3>
                            <p className="mt-1 text-sm text-gray-900">{editedProduct.weight} g</p>
                          </div>
                        )}
                        {editedProduct.purity && editedProduct.purity!=null && editedProduct.purity!=0 && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Purity</h3>
                            <p className="mt-1 text-sm text-gray-900">{editedProduct.purity}K</p>
                          </div>
                        )}
                        {editedProduct.making_charge !== null&& editedProduct.making_charge !== 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Making Charge</h3>
                            <p className="mt-1 text-sm text-gray-900">{editedProduct.making_charge}%</p>
                          </div>
                        )}
                        {editedProduct.quantity && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Quantity</h3>
                            <p className="mt-1 text-sm text-gray-900">{editedProduct.quantity}</p>
                          </div>
                        )}
                      </div>

                      {editedProduct.category && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Category</h3>
                          <p className="mt-1 text-sm text-gray-900 capitalize">{editedProduct.category}</p>
                        </div>
                      )}

                      {editedProduct.description && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Description</h3>
                          <p className="mt-1 text-sm text-gray-900">{editedProduct.description}</p>
                        </div>
                      )}

                      {(editedProduct.owner_name || editedProduct.registered_firm_name) && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Owner Details</h3>
                          <div className="mt-2 space-y-2">
                            {editedProduct.owner_name && (
                              <p className="text-sm text-gray-900">
                                <span className="font-medium">Name:</span> {editedProduct.owner_name}
                              </p>
                            )}
                            {editedProduct.registered_firm_name && (
                              <p className="text-sm text-gray-900">
                                <span className="font-medium">Firm:</span> {editedProduct.registered_firm_name}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {editedProduct.uploadedAt && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Uploaded On</h3>
                          <p className="mt-1 text-sm text-gray-900">
                            {new Date(editedProduct.uploadedAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesalerProductDetails; 